import { currentTabKey } from "../forecastEditor/ForecastEditorDocumentCoordinator.js";
import AccountingCoordinator from "../shared/documentInfrastructure/AccountingCoordinator.js";
import ClosingWrapperWidget from "../../core/widgets/ClosingWrapperWidget/ClosingWrapperWidget.js";
import ExcelExporter from "../reports2/export/ExcelExporter.js";
import ForecastCellInfoWidget from "../forecastEditor/widgets/ForecastCellInfoWidget/ForecastCellInfoWidget.js";
import ForecastDisplaySettingsWidget from "../forecastEditor/widgets/ForecastDisplaySettingsWidget/ForecastDisplaySettingsWidget.js";

/**
 * Model holding all the parts of a forecast viewer document, its
 * widgets and its state. It handles communication between all of
 * these by registering to events and dispatching them via message
 * sends.
 */
export default class ForecastViewerDocumentCoordinator extends AccountingCoordinator {
	async setup({ currentTabId }) {
		await super.setup(...arguments);

		this._drawer.reset();

		history.replaceState({ [currentTabKey]: currentTabId }, "");

		this._title.setSaveSpaceMode(this._documentState.isInSaveSpaceMode());

		this._excelExporter = new ExcelExporter();
	}

	shouldDocumentFitViewport() {
		return true;
	}

	_onPopState({ [currentTabKey]: currentTabId }) {
		this._documentState.setCurrentReportFromCurrentTab(currentTabId);

		this._content.setCurrentTab({
			report: this._documentState.getCurrentReport(),
		});
	}

	_setupDocumentStateCallbacks() {
		return {
			setCurrentTabParameter: this._setCurrentTabParameter.bind(this),
		};
	}

	_setCurrentTabParameter(currentTabId) {
		this._setUrlParameters({ [currentTabKey]: currentTabId });
	}

	_setupContentCallbacks() {
		return {
			onCellClick: this._showForecastCellInfoWidget.bind(this),
			onSettingsButtonClick: this._showSettingsInDrawer.bind(this),
			onExportButtonClick: this._exportForecast.bind(this),
			hideDrawer: () => this._hideDrawer(),
			onBudgetSaved: this._onBudgetSaved.bind(this),
		};
	}

	_exportForecast() {
		const forecast = this._documentState.getForecast();

		const exportingSpec = {
			company: this._documentState.getCompany(),
		};

		this._excelExporter.bulkDownload({
			reports: forecast.getReportsAsList(),
			exportingSpec,
			exportingName: forecast.getName(),
		});
	}

	_showForecastCellInfoWidget({ row, column }) {
		this._showDrawerWith(
			new ClosingWrapperWidget({
				content: this._makeForecastCellInfoWidget({ row, column }),
				onClose: () => this._drawer.hide(),
			}),
		);

		// Make sure the cell is completely visible:
		this._content.scrollToSelectedCell();
	}

	_makeForecastCellInfoWidget({ row, column }) {
		return new ForecastCellInfoWidget({
			documentState: this._documentState,
			row,
			column,
			onReportAndCellSelectionRequest:
				this._content.selectReportAndCell.bind(this._content),
			readOnly: true,
		});
	}

	_showSettingsInDrawer({ report }) {
		this._showDrawerWith(
			new ClosingWrapperWidget({
				content: this._makeForecastDisplaySettingsWidget({ report }),
				onClose: () => this._hideDrawer(),
			}),
		);
	}

	_makeForecastDisplaySettingsWidget({ report }) {
		return new ForecastDisplaySettingsWidget({
			isInSaveSpaceMode: this._documentState.isInSaveSpaceMode(),
			reportDisplaySettings: report.getDisplaySettings(),
			onChange: (settings) =>
				this._setDisplaySettings({ report, settings }),
			onSaveSpaceModeChange: (value) => this._setSaveSpaceMode(value),
			showCreditPositiveSwitch: false,
		});
	}

	_setSaveSpaceMode(value) {
		this._documentState.setSaveSpaceMode(value);
		this._title.setSaveSpaceMode(value);
		this._content.updateSaveSpaceMode();
	}

	_setDisplaySettings({ report, settings }) {
		report.setDisplaySettings(settings);
		report.expandRowsUpToDefaultLevel();

		this._content.updateCurrentReport();
	}

	_onBudgetSaved(budget) {
		this._redirectTo(`budget-editor/${budget.getId()}`);
	}
}
